/* ==================================================================
   ARCHIVO: busqueda-visual.js
   Subir como Asset en: Admin de Shopify → Tienda online →
   Editor de código → Assets → Agregar un activo nuevo
   ------------------------------------------------------------------
   BÚSQUEDA VISUAL DE MUEBLES — Muebles Troncoso
   ------------------------------------------------------------------
   Cómo funciona:
   1. El usuario toma/sube una foto (input file con accept="image/*").
   2. Se carga MobileNet (TensorFlow.js) la PRIMERA vez que se usa
      (se cachea en memoria para no recargar cada vez).
   3. Se clasifica la imagen → se obtiene la clase de ImageNet más
      probable (ej. "studio couch, day bed").
   4. Se mapea esa clase a un término de búsqueda de TU catálogo
      (ej. "sofa") usando MAPEO_MUEBLES.
   5. (MEJORA) Se extrae el color dominante de la foto con <canvas>,
      recortando el fondo para evitar contaminar el promedio, y se
      agrega como término ADICIONAL (con OR, nunca obligatorio).
   6. Se inyecta el query final en el input #buscador y se navega
      directo a /search?q=..., replicando la misma normalización
      (equivalencias, prefijos) que ya usa tu header.js.

   LIMITACIÓN IMPORTANTE (honesta):
   MobileNet es un clasificador GENÉRICO de 1000 categorías de
   ImageNet. NO fue entrenado con tus productos específicos, así que
   NO va a reconocer "el sofá modelo Verona" — solo reconoce la
   categoría general (sofá, mesa, cama, etc.) y, con la mejora de
   color, un color aproximado. Si la foto no corresponde a ninguna
   clase reconocida de mueble, se le pide al usuario que intente con
   otra foto o escriba la búsqueda manual.
   ================================================================== */

(function () {

    "use strict";

    /* ----------------------------------------------------------------
       MAPEO: clase de ImageNet (MobileNet) → término de búsqueda
       Las claves van SIN sinónimos extra; la comparación se hace
       contra la primera frase antes de la coma (ver normalizarClase).
       Fuente verificada: lista oficial de clases ImageNet/ILSVRC.
       ---------------------------------------------------------------- */
    const MAPEO_MUEBLES = {

        // Salas / sillones
        "studio couch":   "sofa",
        "rocking chair":  "sillon",
        "throne":         "sillon",
        "folding chair":  "silla",
        "barber chair":   "silla",

        // Recámaras
        "four-poster":    "cama",
        "cradle":         "cuna",
        "crib":           "cuna",
        "bassinet":       "cuna",

        // Antecomedores / mesas
        "dining table":   "mesa de comedor",
        "desk":           "escritorio",
        "pool table":     "mesa",
        "table lamp":     "lampara",

        // Otros muebles
        "wardrobe":            "ropero",
        "china cabinet":       "vitrina",
        "bookcase":            "librero",
        "chiffonier":          "comoda",
        "medicine chest":      "comoda",
        "entertainment center": "centro de entretenimiento",
        "file":                "cajonera"

    };

    // Umbral mínimo de confianza para aceptar la clasificación.
    const UMBRAL_CONFIANZA = 0.15;

    // Cuántas predicciones top-k pedirle al modelo.
    const TOP_K = 5;

    /* ----------------------------------------------------------------
       DETECCIÓN DE COLOR (mejora opcional, no reemplaza la categoría)
       ------------------------------------------------------------------
       Idea: además de "qué mueble es", sacamos "de qué color es" leyendo
       los píxeles de la foto con <canvas>. Esto se usa como término
       ADICIONAL en el query (con OR), nunca como filtro obligatorio —
       así, si un producto no tiene el color en su título/tags, sigue
       apareciendo igual por la categoría; pero si SÍ lo tiene, Shopify
       lo va a rankear más arriba por relevancia.

       LIMITACIÓN: solo es un promedio de color del centro de la foto,
       no reconoce el modelo/diseño específico del mueble.
       ---------------------------------------------------------------- */
    const PALETA_COLORES = {
        "negro":      [20, 20, 20],
        "blanco":     [240, 240, 235],
        "gris":       [130, 130, 130],
        "gris claro": [190, 190, 190],
        "cafe":       [101, 67, 33],
        "beige":      [222, 196, 160],
        "azul":       [40, 70, 130],
        "verde":      [60, 100, 60],
        "rojo":       [150, 30, 30],
        "amarillo":   [210, 180, 60],
        "rosa":       [220, 160, 170],
        "madera":     [150, 111, 51]
    };

    // % de margen que se recorta de cada lado antes de promediar,
    // para evitar que el fondo/piso/pared contaminen el color
    // detectado (ver pruebas: sin este recorte, un mueble café sobre
    // fondo gris claro de estudio se detectaba como "gris", no "café")
    const MARGEN_RECORTE = 0.3;

    // Qué tan cerca debe estar el color promedio de un color de la
    // paleta para considerarlo confiable. Si el promedio queda muy
    // "a medio camino" entre dos colores (imagen con mucha variación,
    // mueble con textura/patrón), es mejor NO agregar color al query.
    const DISTANCIA_MAXIMA_COLOR = 60;

    let modeloCache = null;
    let modeloCargando = null;

    /* ----------------------------------------------------------------
       Normaliza "studio couch, day bed" -> "studio couch"
       (el modelo regresa sinónimos separados por coma; nos quedamos
       solo con el primero para comparar contra MAPEO_MUEBLES)
       ---------------------------------------------------------------- */
    function normalizarClase(className) {
        return className.split(",")[0].trim().toLowerCase();
    }

    /* ----------------------------------------------------------------
       Dibuja la imagen en un <canvas> oculto y promedia los píxeles
       SOLO del rectángulo central (recortando MARGEN_RECORTE de cada
       lado), para evitar que el fondo de la foto contamine el color
       detectado del mueble.
       ---------------------------------------------------------------- */
    function extraerColorPromedio(imgEl) {

        const TAMANO_MUESTREO = 50; // reescalamos chico, es solo para promediar

        const canvas = document.createElement("canvas");
        canvas.width = TAMANO_MUESTREO;
        canvas.height = TAMANO_MUESTREO;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(imgEl, 0, 0, TAMANO_MUESTREO, TAMANO_MUESTREO);

        const margen = Math.round(TAMANO_MUESTREO * MARGEN_RECORTE);
        const inicio = margen;
        const fin = TAMANO_MUESTREO - margen;
        const ancho = fin - inicio;
        const alto = fin - inicio;

        const datos = ctx.getImageData(inicio, inicio, ancho, alto).data;

        let r = 0, g = 0, b = 0;
        const totalPixeles = ancho * alto;

        for (let i = 0; i < datos.length; i += 4) {
            r += datos[i];
            g += datos[i + 1];
            b += datos[i + 2];
        }

        return [
            Math.round(r / totalPixeles),
            Math.round(g / totalPixeles),
            Math.round(b / totalPixeles)
        ];
    }

    function distanciaColor(c1, c2) {
        return Math.sqrt(
            Math.pow(c1[0] - c2[0], 2) +
            Math.pow(c1[1] - c2[1], 2) +
            Math.pow(c1[2] - c2[2], 2)
        );
    }

    /* ----------------------------------------------------------------
       Clasifica un RGB contra la paleta de colores conocidos.
       Devuelve null si el color promedio no se parece lo suficiente
       a ninguno de la paleta (evita agregar un color "inventado" al
       query cuando la foto es muy variada/con patrones).
       ---------------------------------------------------------------- */
    function clasificarColor(rgb) {

        let mejorNombre = null;
        let mejorDistancia = Infinity;

        for (const [nombre, refRGB] of Object.entries(PALETA_COLORES)) {
            const d = distanciaColor(rgb, refRGB);
            if (d < mejorDistancia) {
                mejorDistancia = d;
                mejorNombre = nombre;
            }
        }

        if (mejorDistancia > DISTANCIA_MAXIMA_COLOR) {
            return null;
        }

        return mejorNombre;
    }

    /* ----------------------------------------------------------------
       Carga (una sola vez) tf.js + el modelo MobileNet desde CDN.
       Se cachea en modeloCache para reusarse en clasificaciones
       posteriores sin volver a descargar nada.
       ---------------------------------------------------------------- */
    function cargarScript(src) {
        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = src;
            script.onload = resolve;
            script.onerror = () => reject(new Error("No se pudo cargar " + src));
            document.head.appendChild(script);
        });
    }

    async function obtenerModelo() {

        if (modeloCache) {
            return modeloCache;
        }

        // Evita cargar el modelo dos veces si el usuario hace doble clic
        if (modeloCargando) {
            return modeloCargando;
        }

        modeloCargando = (async () => {

            if (!window.tf) {
                await cargarScript("https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.20.0/dist/tf.min.js");
            }

            if (!window.mobilenet) {
                await cargarScript("https://cdn.jsdelivr.net/npm/@tensorflow-models/mobilenet@2.1.1/dist/mobilenet.min.js");
            }

            const modelo = await window.mobilenet.load({ version: 2, alpha: 1.0 });

            modeloCache = modelo;

            return modelo;

        })();

        return modeloCargando;

    }

    /* ----------------------------------------------------------------
       Convierte el archivo seleccionado (File) en un <img> ya cargado,
       listo para pasarle a model.classify()
       ---------------------------------------------------------------- */
    function archivoAImagen(file) {
        return new Promise((resolve, reject) => {

            const reader = new FileReader();

            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = () => reject(new Error("No se pudo leer la imagen."));
                img.src = e.target.result;
            };

            reader.onerror = () => reject(new Error("No se pudo leer el archivo."));

            reader.readAsDataURL(file);

        });
    }

    /* ----------------------------------------------------------------
       Recorre las predicciones top-k y regresa el primer término de
       búsqueda que tenga match en MAPEO_MUEBLES (no necesariamente
       la predicción #1, por si la más probable no es un mueble
       reconocido, ej. "wood" o "room").
       ---------------------------------------------------------------- */
    function elegirTermino(predicciones) {

        for (const p of predicciones) {

            if (p.probability < UMBRAL_CONFIANZA) {
                continue;
            }

            const clase = normalizarClase(p.className);

            if (MAPEO_MUEBLES[clase]) {
                return {
                    termino: MAPEO_MUEBLES[clase],
                    claseDetectada: clase,
                    confianza: p.probability
                };
            }
        }

        return null;
    }

    /* ----------------------------------------------------------------
       Dispara la búsqueda navegando directo a /search?q=...

       NOTA TÉCNICA (por qué no usamos form.dispatchEvent ni
       form.submit() a secas):
       - dispatchEvent(new Event("submit")) SÍ ejecuta los listeners
         de addEventListener("submit", ...) — por eso el input se ve
         con el texto ya transformado — pero NO dispara la navegación
         nativa del <form>. Un evento "submit" sintético nunca somete
         el formulario, solo notifica a quien esté escuchando.
       - form.submit() SÍ navega, pero NO ejecuta los listeners de
         submit (por diseño del DOM), así que se perdería toda la
         normalización/equivalencias que ya tienes en header.js.

       Por eso replicamos aquí la misma lógica de normalización que
       usa tu listener de #formBuscador, y navegamos nosotros mismos
       con window.location. Así el comportamiento es idéntico al de
       una búsqueda escrita a mano, incluyendo equivalencias y
       búsqueda por prefijo.

       PARÁMETRO colorDetectado (opcional): si se detectó un color
       confiable en la foto, se agrega como término ADICIONAL con OR,
       nunca como filtro obligatorio. Así, productos sin color en su
       título/tags siguen apareciendo igual (por la categoría), pero
       si SÍ coincide el color, Shopify lo prioriza por relevancia.
       ---------------------------------------------------------------- */
    function dispararBusqueda(termino, colorDetectado) {

        const input = document.getElementById("buscador");
        const form = document.getElementById("formBuscador");

        if (!input || !form) {
            console.error("No se encontró el buscador del header.");
            return;
        }

        const queryBase = construirQueryBusqueda(termino);

        let queryFinal = queryBase;

        if (colorDetectado) {
            const queryColor = construirQueryBusqueda(termino + " " + colorDetectado);
            // Unimos ambos queries con OR, sin duplicar términos
            const terminosCombinados = new Set([
                ...queryBase.split(" OR "),
                ...queryColor.split(" OR ")
            ]);
            queryFinal = [...terminosCombinados].join(" OR ");
        }

        input.value = queryFinal;

        const url = form.getAttribute("action") + "?q=" + encodeURIComponent(queryFinal);

        window.location.href = url;
    }

    /* ----------------------------------------------------------------
       Misma normalización/equivalencias que usa el listener de
       #formBuscador en header.js, replicada aquí porque un evento
       "submit" disparado por JS no activa la navegación del form
       (ver nota arriba). Si en el futuro cambias el diccionario de
       equivalencias en header.js, actualiza también este.
       ---------------------------------------------------------------- */
    function construirQueryBusqueda(valorOriginal) {

        let valor = valorOriginal
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

        let palabras = valor.split(/\s+/).filter(Boolean);

        // Filtra preposiciones/artículos sin valor de búsqueda
        // (tu diccionario de equivalencias no las tiene definidas,
        // así que solo generan ruido tipo "de OR de*" en el query)
        const palabrasVacias = new Set(["de", "del", "la", "el", "los", "las", "y"]);
        palabras = palabras.filter((p) => !palabrasVacias.has(p));

        palabras = palabras.map((palabra) => {
            if (palabra.length > 4) {
                if (palabra.endsWith("es")) {
                    palabra = palabra.slice(0, -2);
                } else if (palabra.endsWith("s")) {
                    palabra = palabra.slice(0, -1);
                }
            }
            return palabra;
        });

        const equivalencias = {
            comedor: ["comedor", "antecomedor"],
            antecomedor: ["antecomedor", "comedor"],
            sala: ["sala", "salas", "sofa", "sofas", "sillon", "sillones"],
            sofa: ["sofa", "sofas", "sillon", "sillones", "sala", "salas"],
            sillon: ["sillon", "sillones", "sofa", "sofas", "sala", "salas"],
            recamara: ["recamara", "recamaras", "cama", "camas"],
            cama: ["cama", "camas", "recamara", "recamaras"],
            buro: ["buro", "buró", "buros", "burós"],
            litera: ["litera"],
            cantina: ["cantina"],
            taburete: ["taburete"],
            banco: ["banco"],
            escritorio: ["escritorio"],
            mesa: ["mesa"],
            silla: ["silla"],
            consola: ["consola"],
            credenza: ["credenza"],
            vitrina: ["vitrina"],
            mueble: ["mueble"]
        };

        let terminosBusqueda = [];

        palabras.forEach((palabra) => {

            terminosBusqueda.push(palabra);

            if (palabra.length >= 2) {
                terminosBusqueda.push(`${palabra}*`);
            }

            if (equivalencias[palabra]) {
                equivalencias[palabra].forEach((eq) => {
                    terminosBusqueda.push(eq);
                    if (eq.length >= 2) {
                        terminosBusqueda.push(`${eq}*`);
                    }
                });
            }
        });

        terminosBusqueda = [...new Set(terminosBusqueda)];

        return terminosBusqueda.join(" OR ");
    }

    /* ----------------------------------------------------------------
       FUNCIÓN PRINCIPAL — exportada a window para llamarse desde el
       input file del header.
       ---------------------------------------------------------------- */
    async function buscarPorImagen(file, callbacks) {

        const {
            onInicio = () => {},
            onSinMatch = () => {},
            onError = () => {},
            onExito = () => {}
        } = callbacks || {};

        try {

            onInicio();

            const [modelo, imagenLista] = await Promise.all([
                obtenerModelo(),
                archivoAImagen(file)
            ]);

            const predicciones = await modelo.classify(imagenLista, TOP_K);

            const resultado = elegirTermino(predicciones);

            if (!resultado) {
                onSinMatch(predicciones);
                return;
            }

            // Extracción de color: best-effort. Si por algún motivo
            // falla (ej. imagen con CORS bloqueado en el canvas), no
            // debe tronar toda la búsqueda — simplemente se busca sin
            // color, que es el comportamiento que ya teníamos.
            let colorDetectado = null;
            try {
                const rgbPromedio = extraerColorPromedio(imagenLista);
                colorDetectado = clasificarColor(rgbPromedio);
            } catch (errorColor) {
                console.warn("No se pudo extraer el color de la imagen:", errorColor);
            }

            onExito({ ...resultado, colorDetectado });

            dispararBusqueda(resultado.termino, colorDetectado);

        } catch (error) {
            console.error("Error en búsqueda por imagen:", error);
            onError(error);
        }
    }

    window.buscarMuebleConFoto = buscarPorImagen;

    /* ----------------------------------------------------------------
       Conecta el <input type="file"> del header con toda la lógica
       anterior, y le da feedback visual simple al usuario mientras
       se procesa (loader + mensajes de error/sin-match).
       Se llama desde el atributo onchange="manejarFotoMueble(this)"
       ---------------------------------------------------------------- */
    window.manejarFotoMueble = function (inputEl) {

        const file = inputEl.files && inputEl.files[0];

        if (!file) {
            return;
        }

        const botonFoto = document.getElementById("botonFoto");

        buscarPorImagen(file, {

            onInicio() {
                if (botonFoto) {
                    botonFoto.classList.add("boton-foto-cargando");
                    botonFoto.disabled = true;
                }
            },

            onExito() {
                if (botonFoto) {
                    botonFoto.classList.remove("boton-foto-cargando");
                    botonFoto.disabled = false;
                }
            },

            onSinMatch() {
                if (botonFoto) {
                    botonFoto.classList.remove("boton-foto-cargando");
                    botonFoto.disabled = false;
                }
                alert("No pudimos identificar un mueble claro en esa foto. Intenta con otra imagen o escribe tu búsqueda directamente.");
            },

            onError() {
                if (botonFoto) {
                    botonFoto.classList.remove("boton-foto-cargando");
                    botonFoto.disabled = false;
                }
                alert("Hubo un problema al analizar la imagen. Por favor intenta de nuevo.");
            }

        });

        // Permite volver a usar el mismo input si el usuario repite
        inputEl.value = "";
    };

})();